import {
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { useQueryClient } from '@tanstack/react-query'
import CloseIcon from '@material-ui/icons/Close'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Container } from '../../components'
import {
    fetchVendorSubPricingDetail,
    getVendorSubPricingErrorMessage,
    vendorSubPricingDetailQueryKeyPrefix,
} from '../../hooks/useSubPricingMatrix'
import { suggestedSubContractPriceWholeDollars } from './SubsPricing/subsPricingUtils'
import { isAccountManager } from '../../helpers'
import { useUser } from '../../hooks'
import { getServiceImageDetails, User, WorkorderStatus } from '../../models'
import type { VendorSubPricingDetailResponse } from '../../models/VendorSubPricing/types'
import { BatchAssignSubPayload, WorkorderResponse } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import { useMultiSelectStyles } from '../../styles'

export interface AssignSubsModalProps {
    isOpen: boolean
    onClose: () => void
    workorders: WorkorderResponse[]
    /** Vendor company id (active workspace id) for sub-contract pricing-detail requests. */
    vendorId: number | null
    userList: User[]
    /** True while fetching `user/` with `my_team: true` (Workforce My Team). */
    userListLoading?: boolean
    batchAssignSubcontractors: (
        payload: BatchAssignSubPayload,
    ) => Promise<WorkorderResponse[]>
    submitting: boolean
    onSuccess: () => void
}

type RowState = {
    workorderId: number
    workorder: WorkorderResponse
    userId: number
    priceInput: string
}

const buildRowsFromWorkorders = (workorders: WorkorderResponse[]): RowState[] =>
    workorders.map((wo) => {
        const expenseRounded = Math.max(
            0,
            Math.round(Number(wo.sub_contract?.payout_amount ?? 0)),
        )
        const existingWorkerId = wo.sub_contract?.assigned_worker_id
        return {
            workorderId: wo.id,
            workorder: wo,
            userId:
                existingWorkerId != null && existingWorkerId > 0
                    ? existingWorkerId
                    : -1,
            priceInput: expenseRounded === 0 ? '' : String(expenseRounded),
        }
    })

const formatWorkorderSummary = (wo: WorkorderResponse): string => {
    const service = wo.service_name?.trim() || 'Service'
    const unit = wo.unit_name?.trim() || '—'
    const areas =
        wo.service_area_list
            ?.map((a) => a.area_name)
            .filter(Boolean)
            .join(', ') ?? ''
    return areas ? `${service} · ${unit} · ${areas}` : `${service} · ${unit}`
}

const isValidWholeDollar = (s: string): boolean => {
    if (s === '' || s === '-') return false
    if (!/^-?\d+$/.test(s)) return false
    const n = Number(s)
    return Number.isFinite(n) && n >= 0
}

/** Empty means $0 on submit; non-empty must be whole dollars ≥ 0. */
const isPriceInputValidForSubmit = (s: string): boolean => {
    const t = s.trim()
    if (t === '') return true
    return isValidWholeDollar(t) && Number(t) >= 0
}

/** Sub user can only be (re)assigned on Assigned workorders, unless the user is an account manager. */
const isAssignableForSub = (
    wo: WorkorderResponse,
    accountManager = false,
): boolean => accountManager || wo.status === WorkorderStatus.ASSIGNED

export const AssignSubsModal = ({
    isOpen,
    onClose,
    workorders,
    vendorId,
    userList,
    userListLoading = false,
    batchAssignSubcontractors,
    submitting,
    onSuccess,
}: AssignSubsModalProps) => {
    const theme = useTheme()
    const queryClient = useQueryClient()
    const classes = useMultiSelectStyles()
    const { workspaceUser } = useUser()
    const accountManager = isAccountManager(workspaceUser)
    const [rows, setRows] = useState<RowState[]>([])
    const [assignAllUserId, setAssignAllUserId] = useState<number>(-1)
    const [applyingContractPricing, setApplyingContractPricing] = useState(
        false,
    )
    const pricingSessionRef = useRef(0)
    const workordersRef = useRef(workorders)
    workordersRef.current = workorders

    useEffect(() => {
        if (!isOpen) {
            pricingSessionRef.current += 1
            setRows([])
            setAssignAllUserId(-1)
            setApplyingContractPricing(false)
            return
        }

        setRows(buildRowsFromWorkorders(workordersRef.current))
        setAssignAllUserId(-1)
        setApplyingContractPricing(false)
    }, [isOpen])

    useEffect(() => {
        if (!isOpen || userListLoading) {
            return
        }
        const ids = new Set(userList.map((u) => u.id))
        setAssignAllUserId((prev) => (prev > 0 && !ids.has(prev) ? -1 : prev))
    }, [isOpen, userList, userListLoading])

    const assignableRows = useMemo(
        () =>
            rows.filter((r) => isAssignableForSub(r.workorder, accountManager)),
        [accountManager, rows],
    )

    const submitableRows = useMemo(() => rows.filter((r) => r.userId > 0), [
        rows,
    ])

    const canSubmit = useMemo(() => {
        if (userListLoading) return false
        if (submitableRows.length === 0) return false
        // Every Assigned row must have a sub selected before submit.
        if (assignableRows.some((r) => r.userId <= 0)) return false
        return submitableRows.every((r) =>
            isPriceInputValidForSubmit(r.priceInput),
        )
    }, [assignableRows, submitableRows, userListLoading])

    const rowsWithUserCount = useMemo(
        () => rows.filter((r) => r.userId > 0).length,
        [rows],
    )

    const assignAllDisabled =
        submitting ||
        userListLoading ||
        applyingContractPricing ||
        assignableRows.length === 0

    const applyContractPricingDisabled =
        submitting ||
        userListLoading ||
        applyingContractPricing ||
        vendorId == null ||
        rowsWithUserCount === 0

    const applySuggestedPriceForDetail = useCallback(
        (wo: WorkorderResponse, detail: VendorSubPricingDetailResponse) => {
            const rounded = suggestedSubContractPriceWholeDollars(detail, wo)
            if (rounded === null) return null
            return rounded === 0 ? '' : String(rounded)
        },
        [],
    )

    /** Fetch contract pricing for unique (userId, apartmentId) pairs and apply to matching rows. */
    const applyContractPricingToRows = useCallback(
        async (
            targetRows: RowState[],
            /** When set, only update rows whose userId matches (e.g. after Assign all). */
            restrictToUserId?: number,
        ) => {
            if (vendorId == null) return

            const rowsToPrice = targetRows.filter((r) => {
                if (r.userId <= 0) return false
                if (restrictToUserId != null && r.userId !== restrictToUserId) {
                    return false
                }
                return true
            })
            if (rowsToPrice.length === 0) return

            const session = pricingSessionRef.current
            const detailByKey = new Map<
                string,
                VendorSubPricingDetailResponse
            >()
            const uniqueKeys: { userId: number; apartmentId: number }[] = []
            const seen = new Set<string>()
            for (const r of rowsToPrice) {
                const key = `${r.userId}:${r.workorder.apartment_id}`
                if (seen.has(key)) continue
                seen.add(key)
                uniqueKeys.push({
                    userId: r.userId,
                    apartmentId: r.workorder.apartment_id,
                })
            }

            for (const { userId, apartmentId } of uniqueKeys) {
                const detail = await queryClient.fetchQuery({
                    queryKey: [
                        ...vendorSubPricingDetailQueryKeyPrefix,
                        vendorId,
                        userId,
                        apartmentId,
                    ],
                    queryFn: ({ signal }) =>
                        fetchVendorSubPricingDetail(
                            {
                                vendor_id: vendorId,
                                sub_vendor_user_id: userId,
                                apartment_id: apartmentId,
                            },
                            signal,
                        ),
                })
                detailByKey.set(`${userId}:${apartmentId}`, detail)
            }

            if (session !== pricingSessionRef.current) {
                return
            }

            setRows((prev) =>
                prev.map((r) => {
                    if (r.userId <= 0) return r
                    if (
                        restrictToUserId != null &&
                        r.userId !== restrictToUserId
                    ) {
                        return r
                    }
                    const detail = detailByKey.get(
                        `${r.userId}:${r.workorder.apartment_id}`,
                    )
                    if (!detail) return r
                    const priceInput = applySuggestedPriceForDetail(
                        r.workorder,
                        detail,
                    )
                    if (priceInput === null) return r
                    return { ...r, priceInput }
                }),
            )
        },
        [applySuggestedPriceForDetail, queryClient, vendorId],
    )

    const handleAssignAllChange = (userId: number) => {
        setAssignAllUserId(userId)
        if (userId <= 0) {
            return
        }

        const nextRows = rows.map((r) =>
            isAssignableForSub(r.workorder, accountManager)
                ? { ...r, userId }
                : r,
        )
        setRows(nextRows)

        if (vendorId == null) {
            return
        }

        void (async () => {
            try {
                await applyContractPricingToRows(nextRows, userId)
            } catch (e: unknown) {
                toast.error(getVendorSubPricingErrorMessage(e))
            }
        })()
    }

    const handleApplyContractPricing = () => {
        if (applyContractPricingDisabled) return
        void (async () => {
            setApplyingContractPricing(true)
            try {
                await applyContractPricingToRows(rows)
            } catch (e: unknown) {
                toast.error(getVendorSubPricingErrorMessage(e))
            } finally {
                setApplyingContractPricing(false)
            }
        })()
    }

    const handleRowUserChange = useCallback(
        (workorderId: number, wo: WorkorderResponse, newUserId: number) => {
            setRows((prev) =>
                prev.map((r) =>
                    r.workorderId === workorderId
                        ? { ...r, userId: newUserId }
                        : r,
                ),
            )

            if (newUserId <= 0 || vendorId == null) {
                return
            }

            const session = pricingSessionRef.current

            void queryClient
                .fetchQuery({
                    queryKey: [
                        ...vendorSubPricingDetailQueryKeyPrefix,
                        vendorId,
                        newUserId,
                        wo.apartment_id,
                    ],
                    queryFn: ({ signal }) =>
                        fetchVendorSubPricingDetail(
                            {
                                vendor_id: vendorId,
                                sub_vendor_user_id: newUserId,
                                apartment_id: wo.apartment_id,
                            },
                            signal,
                        ),
                })
                .then((detail) => {
                    if (session !== pricingSessionRef.current) {
                        return
                    }
                    const priceInput = applySuggestedPriceForDetail(wo, detail)
                    if (priceInput === null) return
                    setRows((prev) =>
                        prev.map((r) => {
                            if (
                                r.workorderId !== workorderId ||
                                r.userId !== newUserId
                            ) {
                                return r
                            }
                            return { ...r, priceInput }
                        }),
                    )
                })
                .catch((e: unknown) => {
                    toast.error(getVendorSubPricingErrorMessage(e))
                })
        },
        [applySuggestedPriceForDetail, queryClient, vendorId],
    )

    const handleSubmit = async () => {
        if (!canSubmit || submitting || userListLoading) return
        const payload: BatchAssignSubPayload = {
            assignments: submitableRows.map((r) => ({
                workorder_id: r.workorderId,
                user_id: r.userId,
                sub_price:
                    r.priceInput.trim() === '' ? 0 : Number(r.priceInput),
            })),
        }
        try {
            await batchAssignSubcontractors(payload)
            onSuccess()
            onClose()
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response
                    ?.data?.message ?? 'Could not assign subcontractors.'
            toast.error(msg)
        }
    }

    const handleClose = () => {
        if (!submitting) {
            onClose()
        }
    }

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={isOpen}>
                <Paper
                    style={{
                        width: '90%',
                        maxWidth: 560,
                        display: 'flex',
                        overflow: 'hidden',
                        flexDirection: 'column',
                        maxHeight: '90%',
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            padding: theme.spacing(1, 2),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            Assign Subs
                        </Typography>
                        <div style={{ flex: 1 }} />
                        <Tooltip title="Cancel">
                            <IconButton
                                onClick={handleClose}
                                disabled={submitting}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Container>

                    <div
                        style={{
                            padding: theme.spacing(2),
                            overflowY: 'auto',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            {accountManager ? (
                                <>
                                    Account managers can assign a sub on
                                    workorders in any status. Use{' '}
                                    <strong>Apply contract pricing</strong> to
                                    fill prices from each sub&apos;s contract.
                                </>
                            ) : (
                                <>
                                    Sub assignment is only available for
                                    workorders in <strong>Assigned</strong>{' '}
                                    status. Pricing can be edited for any status
                                    when a sub is already set. Use{' '}
                                    <strong>Apply contract pricing</strong> to
                                    fill prices from each sub&apos;s contract.
                                </>
                            )}
                        </Typography>

                        {!userListLoading &&
                            rows.length > 0 &&
                            submitableRows.length === 0 && (
                                <Typography variant="body2" color="error">
                                    {accountManager
                                        ? 'No rows can be submitted. Assign a sub on each workorder, or select workorders that already have a sub to edit pricing.'
                                        : 'No rows can be submitted. Assign a sub on Assigned workorders, or select workorders that already have a sub to edit pricing.'}
                                </Typography>
                            )}

                        {userListLoading && (
                            <Container
                                style={{
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                }}
                            >
                                <CircularProgress size={32} />
                            </Container>
                        )}

                        <FormControl
                            className={classes.formControl}
                            variant="outlined"
                            fullWidth
                            style={{
                                margin: theme.spacing(0, 0, 1, 0),
                                maxWidth: 'none',
                            }}
                        >
                            <InputLabel id="assign-all-user-label">
                                Assign all
                            </InputLabel>
                            <Select
                                labelId="assign-all-user-label"
                                label="Assign all"
                                value={assignAllUserId}
                                onChange={(e) =>
                                    handleAssignAllChange(
                                        e.target.value as number,
                                    )
                                }
                                disabled={assignAllDisabled}
                            >
                                <MenuItem value={-1}>
                                    <em>Select user</em>
                                </MenuItem>
                                {userList.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip
                            title={
                                vendorId == null
                                    ? 'Workspace required for contract pricing'
                                    : rowsWithUserCount === 0
                                    ? 'Select a sub on at least one row first'
                                    : "Fill each row's price from that sub's contract rates"
                            }
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    disabled={applyContractPricingDisabled}
                                    onClick={handleApplyContractPricing}
                                    style={{ textTransform: 'none' }}
                                >
                                    {applyingContractPricing
                                        ? 'Applying contract pricing…'
                                        : 'Apply contract pricing'}
                                </Button>
                            </span>
                        </Tooltip>

                        {!userListLoading && userList.length === 0 && (
                            <Typography variant="body2" color="error">
                                No team members loaded. You may need to refresh
                                or check workspace settings.
                            </Typography>
                        )}

                        {rows.map((row) => {
                            const assignable = isAssignableForSub(
                                row.workorder,
                                accountManager,
                            )
                            const hasSub = row.userId > 0
                            const serviceVisual = getServiceImageDetails(
                                row.workorder.service_id ?? -1,
                                row.workorder.service_category,
                            )
                            const serviceIconSrc =
                                serviceVisual.icon ?? serviceVisual.src
                            const busy =
                                submitting ||
                                userListLoading ||
                                applyingContractPricing
                            const userSelectDisabled = busy || !assignable
                            const priceDisabled =
                                busy || (!assignable && !hasSub)
                            const lockedUserLabel =
                                row.workorder.sub_contract
                                    ?.assigned_worker_name ??
                                userList.find((u) => u.id === row.userId)
                                    ?.name ??
                                `User ${row.userId}`
                            return (
                                <Paper
                                    key={row.workorderId}
                                    variant="outlined"
                                    style={{
                                        padding: theme.spacing(2),
                                    }}
                                >
                                    <Container
                                        style={{
                                            alignItems: 'flex-start',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                marginRight: theme.spacing(1),
                                            }}
                                        >
                                            {serviceIconSrc ? (
                                                <img
                                                    alt=""
                                                    src={serviceIconSrc}
                                                    style={{
                                                        maxHeight: 25,
                                                        maxWidth: 27,
                                                        marginRight: theme.spacing(
                                                            1,
                                                        ),
                                                        objectFit: 'contain',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: 27,
                                                        height: 25,
                                                        marginRight: theme.spacing(
                                                            1,
                                                        ),
                                                        borderRadius: 1,
                                                        backgroundColor:
                                                            row.workorder
                                                                .service_color ??
                                                            theme.palette
                                                                .grey[400],
                                                    }}
                                                />
                                            )}
                                        </Container>
                                        <Container
                                            direction="column"
                                            style={{ flex: 1, minWidth: 0 }}
                                        >
                                            <Typography variant="subtitle2">
                                                {formatWorkorderSummary(
                                                    row.workorder,
                                                )}
                                            </Typography>
                                            {!assignable ? (
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    style={{
                                                        display: 'block',
                                                        marginTop: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {hasSub
                                                        ? `Status is ${row.workorder.status} — sub is locked; pricing can still be edited.`
                                                        : `Only Assigned workorders can be assigned a sub. This one is ${row.workorder.status}.`}
                                                </Typography>
                                            ) : null}
                                        </Container>
                                        <Button
                                            color="secondary"
                                            size="small"
                                            disabled={
                                                submitting ||
                                                applyingContractPricing
                                            }
                                            onClick={() =>
                                                setRows((prev) =>
                                                    prev.filter(
                                                        (r) =>
                                                            r.workorderId !==
                                                            row.workorderId,
                                                    ),
                                                )
                                            }
                                            style={{
                                                textTransform: 'none',
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Container>

                                    <Container
                                        style={{ alignItems: 'flex-end' }}
                                    >
                                        <FormControl
                                            className={classes.formControl}
                                            variant="outlined"
                                            style={{
                                                flex: 1,
                                                marginRight: theme.spacing(2),
                                                maxWidth: 'none',
                                                marginTop: 0,
                                                marginBottom: 0,
                                            }}
                                        >
                                            <InputLabel
                                                id={`user-label-${row.workorderId}`}
                                            >
                                                Select user
                                            </InputLabel>
                                            <Select
                                                labelId={`user-label-${row.workorderId}`}
                                                label="Select user"
                                                value={row.userId}
                                                onChange={(e) =>
                                                    handleRowUserChange(
                                                        row.workorderId,
                                                        row.workorder,
                                                        e.target
                                                            .value as number,
                                                    )
                                                }
                                                disabled={userSelectDisabled}
                                            >
                                                <MenuItem value={-1}>
                                                    <em>Select user</em>
                                                </MenuItem>
                                                {hasSub &&
                                                    !userList.some(
                                                        (u) =>
                                                            u.id === row.userId,
                                                    ) && (
                                                        <MenuItem
                                                            value={row.userId}
                                                        >
                                                            {lockedUserLabel}
                                                        </MenuItem>
                                                    )}
                                                {userList.map((user) => (
                                                    <MenuItem
                                                        key={user.id}
                                                        value={user.id}
                                                    >
                                                        {user.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            label="Price"
                                            variant="outlined"
                                            size="small"
                                            value={row.priceInput}
                                            style={{ width: 120 }}
                                            disabled={priceDisabled}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        $
                                                    </InputAdornment>
                                                ),
                                            }}
                                            onFocus={() => {
                                                if (row.priceInput === '0') {
                                                    setRows((prev) =>
                                                        prev.map((r) =>
                                                            r.workorderId ===
                                                            row.workorderId
                                                                ? {
                                                                      ...r,
                                                                      priceInput:
                                                                          '',
                                                                  }
                                                                : r,
                                                        ),
                                                    )
                                                }
                                            }}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                if (
                                                    v === '' ||
                                                    /^[0-9]*$/.test(v)
                                                ) {
                                                    setRows((prev) =>
                                                        prev.map((r) =>
                                                            r.workorderId ===
                                                            row.workorderId
                                                                ? {
                                                                      ...r,
                                                                      priceInput: v,
                                                                  }
                                                                : r,
                                                        ),
                                                    )
                                                }
                                            }}
                                        />
                                    </Container>
                                </Paper>
                            )
                        })}
                    </div>

                    <div
                        style={{
                            padding: theme.spacing(2),
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={
                                !canSubmit ||
                                submitting ||
                                userListLoading ||
                                applyingContractPricing
                            }
                            onClick={handleSubmit}
                            style={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                textTransform: 'none',
                            }}
                        >
                            {submitting ? 'Submitting…' : 'Submit assignments'}
                        </Button>
                    </div>
                </Paper>
            </Slide>
        </Modal>
    )
}
